import {
  Fragment,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import { colors, s16 } from '@ardoq/design-tokens';
import { Box, Stack } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import { UnstyledButton } from '@ardoq/button';
import { UnstyledLink, type UnstyledLinkProps } from '@ardoq/link';

const SECTION_ID_ATTRIBUTE = 'data-toc-section-id';

export type TableOfContentsEntry<Id extends string = string> = {
  label: string;
  /** Nested entries, rendered indented below this one. Any depth. */
  children?: TableOfContentsEntry<Id>[];
} & (ButtonNavEntry<Id> | LinkNavEntry<Id> | NonClickableNavEntry);

type ButtonNavEntry<Id extends string = string> = {
  type: 'button';
  id: Id;
  onClick: VoidFunction;
};

type LinkNavEntry<Id extends string = string> = UnstyledLinkProps & {
  type: 'link';
  id: Id;
};

type NonClickableNavEntry = {
  type: 'header';
  id?: never;
  onClick?: never;
};

export type TableOfContentsSidebarProps<Id extends string = string> = {
  entries: TableOfContentsEntry<Id>[];
  /** Accessible label for the table-of-contents nav landmark. */
  navAriaLabel: string;
  /** Optional header rendered above the entries. */
  title?: string;
  /** Which side the nav sits on. Defaults to 'left'. */
  navPosition?: 'left' | 'right';
  /**
   * The scrollable content. Wrap each section in <TableOfContentsSection> with
   * an id matching an entry.
   */
  children: ReactNode;
};

export type TableOfContentsSectionProps<Id extends string = string> = {
  id: Id;
  children: ReactNode;
};

export const TableOfContentsSection = <Id extends string = string>({
  id,
  children,
}: TableOfContentsSectionProps<Id>) => (
  <div {...{ [SECTION_ID_ATTRIBUTE]: id }}>{children}</div>
);

// The whole area scrolls (not just the content column) so the scrollbar sits at
// the outer edge rather than between the content and the nav.
const Container = styled.div`
  display: flex;
  gap: ${s16};
  height: 100%;
  min-height: 0;
  overflow-y: auto;
`;

const Nav = styled.nav`
  position: sticky;
  top: 0;
  align-self: flex-start;
  flex-shrink: 0;
`;

const navEntryStyles = css`
  display: block;
  width: 100%;
  text-align: left;
  color: inherit;
  border-left: 2px solid transparent;
  border-radius: 0 4px 4px 0;

  &:hover {
    background: ${colors.surfaceHover};
  }

  &[aria-current='location'] {
    border-left-color: ${colors.borderBrand};
    background: ${colors.surfaceActive};
  }
`;

const NavEntryButton = styled(UnstyledButton)`
  ${navEntryStyles}
`;

const NavEntryLink = styled(UnstyledLink)`
  ${navEntryStyles}
`;

/**
 * Fraction of the container height that defines the "reading line". A section
 * becomes active once its top edge scrolls above this line — kept near the top
 * so the section being read sits toward the top of the viewport.
 */
const ACTIVATION_RATIO = 0.15;

const getActiveSectionId = (
  container: HTMLDivElement,
  orderedIds: string[]
): string | null => {
  const isScrolledToTop = container.scrollTop <= 0;
  if (isScrolledToTop) {
    return orderedIds[0] ?? null;
  }

  const isScrolledToBottom =
    container.scrollTop + container.clientHeight >= container.scrollHeight - 1;
  if (isScrolledToBottom) {
    return orderedIds.at(-1) ?? null;
  }

  const readingLine =
    container.getBoundingClientRect().top +
    container.clientHeight * ACTIVATION_RATIO;
  const lastPassedSection = Array.from(
    container.querySelectorAll(`[${SECTION_ID_ATTRIBUTE}]`)
  )
    .filter(section => section.getBoundingClientRect().top <= readingLine)
    .at(-1);

  return (
    lastPassedSection?.getAttribute(SECTION_ID_ATTRIBUTE) ??
    orderedIds[0] ??
    null
  );
};

const useActiveSection = (
  orderedIds: string[]
): {
  activeId: string | null;
  containerRef: RefObject<HTMLDivElement | null>;
} => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<string | null>(
    orderedIds[0] ?? null
  );

  const orderedIdsKey = orderedIds.join('|');

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let frame = 0;
    const updateActiveSection = () => {
      const nextActiveId = getActiveSectionId(container, orderedIds);
      if (nextActiveId) {
        setActiveId(nextActiveId);
      }
    };
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateActiveSection);
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    updateActiveSection();

    return () => {
      cancelAnimationFrame(frame);
      container.removeEventListener('scroll', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- orderedIds is tracked via orderedIdsKey
  }, [orderedIdsKey]);

  return { activeId, containerRef };
};

const collectSectionIds = <Id extends string>(
  entries: TableOfContentsEntry<Id>[]
): Id[] =>
  entries.flatMap(entry => [
    ...(entry.id !== undefined ? [entry.id] : []),
    ...(entry.children ? collectSectionIds(entry.children) : []),
  ]);

const NavEntryLabel = ({
  label,
  isActive,
}: {
  label: string;
  isActive: boolean;
}) => (
  <Box paddingY="xsmall" paddingX="small">
    <Text
      variant={isActive ? 'text2Bold' : 'text2'}
      color={isActive ? 'textDefault' : 'textSubtle'}
    >
      {label}
    </Text>
  </Box>
);

const NavEntryRow = <Id extends string>({
  entry,
  isActive,
}: {
  entry: TableOfContentsEntry<Id>;
  isActive: boolean;
}) => {
  if (entry.type === 'header') {
    return (
      <Box paddingY="xsmall" paddingX="small">
        <Text variant="caption" color="textSubtle">
          {entry.label}
        </Text>
      </Box>
    );
  }

  if (entry.type === 'link') {
    return (
      <NavEntryLink
        href={entry.href}
        target={entry.target}
        onClick={entry.onClick}
        onTrack={entry.onTrack}
        aria-current={isActive ? 'location' : undefined}
      >
        <NavEntryLabel label={entry.label} isActive={isActive} />
      </NavEntryLink>
    );
  }

  return (
    <NavEntryButton
      onClick={entry.onClick}
      aria-current={isActive ? 'location' : undefined}
    >
      <NavEntryLabel label={entry.label} isActive={isActive} />
    </NavEntryButton>
  );
};

const NavEntries = <Id extends string>({
  entries,
  activeId,
}: {
  entries: TableOfContentsEntry<Id>[];
  activeId: string | null;
}) => (
  <Stack gap="xxsmall">
    {entries.map((entry, index) => {
      const isActive = entry.id !== undefined && entry.id === activeId;
      return (
        <Fragment key={entry.id ?? `heading-${entry.label}-${index}`}>
          <NavEntryRow entry={entry} isActive={isActive} />
          {entry.children && (
            <Box paddingLeft="medium">
              <NavEntries entries={entry.children} activeId={activeId} />
            </Box>
          )}
        </Fragment>
      );
    })}
  </Stack>
);

export const TableOfContentsSidebar = <Id extends string = string>({
  entries,
  navAriaLabel,
  title,
  navPosition = 'left',
  children,
}: TableOfContentsSidebarProps<Id>) => {
  const { activeId, containerRef } = useActiveSection(
    collectSectionIds(entries)
  );

  const nav = (
    <Navigation
      entries={entries}
      navAriaLabel={navAriaLabel}
      activeId={activeId}
      title={title}
    />
  );

  return (
    <Container ref={containerRef}>
      {navPosition === 'left' && nav}
      <Box flexGrow={1} minWidth="0" padding="medium">
        {children}
      </Box>
      {navPosition === 'right' && nav}
    </Container>
  );
};

type NavigationProps = {
  entries: TableOfContentsEntry[];
  activeId: string | null;
  navAriaLabel: string;
  title?: string;
};

const Navigation = (props: NavigationProps) => (
  <Nav aria-label={props.navAriaLabel}>
    <Stack gap="xxsmall">
      {props.title !== undefined && (
        <Box paddingX="small" paddingY="xsmall">
          <Text variant="caption" color="textSubtle">
            {props.title}
          </Text>
        </Box>
      )}
      <NavEntries entries={props.entries} activeId={props.activeId} />
    </Stack>
  </Nav>
);
